import { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { FROM_DATE_LIMIT } from "./DatePicker.consts";
import DateTimeContext from "../../../contexts/DateTimeContext";
import dayjs from "dayjs";

const usePredefinedDatePicker = (
  dates,
  setDates,
  fromDateLimit,
  allowSearchParams,
  fromKeyword,
  toKeyword,
  testId,
  presetOptions
) => {
  const { getTimeZoneDate, timeZone } = useContext(DateTimeContext);
  const dateOptions = useMemo(() => {
    if (presetOptions) return presetOptions;
    const now = dayjs().tz(timeZone);
    return [
      {
        key: "today",
        label: "Today",
        startDate: now.startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_today`
      },
      {
        key: "yesterday",
        label: "Yesterday",
        startDate: now.subtract(1, "d").startOf("d"),
        endDate: now.subtract(1, "d").endOf("d"),
        testId: `${testId}_yesterday`
      },
      {
        key: "last_7_days",
        label: "Last 7 Days",
        startDate: now.subtract(7, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_7_days`
      },
      {
        key: "last_14_days",
        label: "Last 14 Days",
        startDate: now.subtract(14, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_14_days`
      },
      {
        key: "last_30_days",
        label: "Last 30 Days",
        startDate: now.subtract(30, "d").startOf("d"),
        endDate: now.endOf("d"),
        testId: `${testId}_last_30_days`
      }
    ];
  }, [timeZone, testId, presetOptions]);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [localDates, setLocalDates] = useState([...dates]);
  const [selectedDate, setSelectedDate] = useState(dateOptions[2]);

  useEffect(() => {
    let tempDates = [...dates];

    if (allowSearchParams) {
      if (searchParams?.get(fromKeyword)) {
        const fromDate = dayjs(searchParams?.get(fromKeyword)).tz(timeZone);
        tempDates = [fromDate, tempDates[1]];
      }

      if (searchParams?.get(toKeyword)) {
        const toDate = dayjs(searchParams?.get(toKeyword)).tz(timeZone);
        tempDates = [tempDates[0], toDate];
      }
    }

    const predefinedDateSelected = dateOptions.find(
      dateOption =>
        tempDates
        && dateOption.startDate.format() === tempDates[0].format()
        && dateOption.endDate.format() === tempDates[1].format()
    );
    setSelectedDate(predefinedDateSelected);

    if ((tempDates[0] && tempDates[0].format() !== dates[0].format())
        || (tempDates[1] && tempDates[1]?.format() !== dates[1].format())) {
      setDates(tempDates[0], tempDates[1]);
      setLocalDates(tempDates);
    }

    if (allowSearchParams && predefinedDateSelected) {
      updateSearchParams(predefinedDateSelected);
    }

  }, [dates, allowSearchParams, dateOptions]);

  const updateSearchParams = (dateOption) => {
    const nextFrom = dateOption.startDate.format();
    const nextTo = dateOption.endDate.format();
    if (searchParams.get(fromKeyword) === nextFrom && searchParams.get(toKeyword) === nextTo) return;
    searchParams.set(fromKeyword, nextFrom);
    searchParams.set(toKeyword, nextTo);
    setSearchParams(searchParams);
  };

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const selectDate = (dateOption, closeMenu, isDateInvalid) => {
    if (closeMenu) {
      handleCloseMenu();
    }

    if (!isDateInvalid) {
      if (allowSearchParams) {
        updateSearchParams(dateOption);
      }
      setLocalDates([dateOption.startDate, dateOption.endDate]);
      setDates(dateOption.startDate, dateOption.endDate);
    }
  };

  const isBeforeLimit = date => {
    if (fromDateLimit === FROM_DATE_LIMIT.ONE_YEAR) {
      return date < getTimeZoneDate().subtract(13, "months");
    }
    if (fromDateLimit === FROM_DATE_LIMIT.ONE_HUNDRED_EIGHTY_DAYS) {
      return date < getTimeZoneDate().subtract(180, "d");
    }
    return date < getTimeZoneDate().subtract(90, "d");
  };

  const shouldDisableFromDate = date => isBeforeLimit(date);

  const shouldDisableToDate = date =>
    isBeforeLimit(date)
    || (dates[0] && date.endOf("d").valueOf() < dates[0].startOf("d").valueOf());

  const isMenuOpen = Boolean(menuAnchorEl);

  const isMenuItemSelected = dateOption =>
    !!selectedDate && selectedDate.key === dateOption.key;

  return {
    dateOptions,
    selectedDate,
    localDates,
    isMenuOpen,
    menuAnchorEl,
    isMenuItemSelected,
    handleOpenMenu,
    handleCloseMenu,
    selectDate,
    shouldDisableFromDate,
    shouldDisableToDate
  };

};

export default usePredefinedDatePicker;
